import { useCallback } from 'react';
import { uazapiService } from '../../../services/uazapiService';
import { createModuleLogger } from '../../../utils/logger';
import { uploadChatFileToR2 } from '../lib/uploadChatFile';

const log = createModuleLogger('[MediaUpload]');

interface UploadResult {
    url: string;
    type: 'image' | 'video' | 'audio' | 'document';
}

interface SendMediaResult {
    messageId?: string;
    success: boolean;
}

export function useMediaUpload(instanceName: string | undefined) {
    /**
     * Determina o tipo de mídia baseado no MIME type.
     */
    const getMediaType = useCallback((file: File): 'image' | 'video' | 'audio' | 'document' => {
        if (file.type.startsWith('image/')) return 'image';
        // Se for vídeo, só aceita 'video' se for mp4. Senão, vai como arquivo normal (document)
        if (file.type === 'video/mp4') return 'video';
        if (file.type.startsWith('video/')) return 'document'; // videos não-mp4 como doc
        if (file.type.startsWith('audio/')) return 'audio';
        return 'document';
    }, []);

    /**
     * Faz upload do arquivo para Cloudflare R2 via Edge Function.
     */
    const uploadToR2 = useCallback(async (file: File): Promise<string> => {
        log.log('📤 Uploading file:', file.name, file.type);
        const url = await uploadChatFileToR2(file);
        log.log('✅ Arquivo enviado para R2:', url);
        return url;
    }, []);

    /**
     * Envia mídia via Uazapi API.
     */
    const sendViaUazapi = useCallback(async (
        type: 'image' | 'video' | 'audio' | 'document',
        mediaSource: string,
        number: string,
        filename: string,
        mimeType: string,
        caption?: string,
        requestId?: string,
        onAccepted?: () => void,
    ): Promise<SendMediaResult> => {
        if (!instanceName) {
            throw new Error('Instance name não configurado');
        }

        // Para vídeos não-MP4 enviados como documento, usar MIME genérico
        // para evitar que a Uazapi tente processar como vídeo (causa erro 500)
        let safeMimeType = mimeType;
        if (type === 'document' && mimeType.startsWith('video/')) {
            safeMimeType = 'application/octet-stream';
        }

        log.log('📤 Enviando via Uazapi:', { type, source: mediaSource.startsWith('data:') ? 'base64' : mediaSource.substring(0, 60), number, filename, safeMimeType });

        let response;

        try {
            switch (type) {
                case 'image':
                    response = await uazapiService.sendImage(instanceName, number, mediaSource, caption, safeMimeType, requestId, onAccepted);
                    break;
                case 'video':
                    response = await uazapiService.sendVideo(instanceName, number, mediaSource, caption, safeMimeType, requestId, onAccepted);
                    break;
                case 'audio':
                    response = await uazapiService.sendAudio(instanceName, number, mediaSource, safeMimeType, requestId, onAccepted);
                    break;
                default:
                    response = await uazapiService.sendDocument(instanceName, number, mediaSource, filename, safeMimeType, caption, requestId, onAccepted);
            }

            log.log('✅ Resposta da API:', response);

            if (!response || response.error) {
                const errorMsg = response?.error || 'Resposta vazia';
                // Mensagem amigável para erros comuns
                if (typeof errorMsg === 'string' && errorMsg.includes('500')) {
                    throw new Error(`Formato não suportado ou arquivo muito grande. Tente converter para MP4 (vídeos) ou PDF (documentos).`);
                }
                throw new Error(errorMsg);
            }

        } catch (apiError: any) {
            log.error('❌ Erro ao chamar Uazapi API:', apiError);
            throw apiError;
        }

        return {
            messageId: response?.key?.id,
            success: true
        };
    }, [instanceName]);

    /**
     * Processo completo: upload + envio via API.
     */
    const uploadAndSend = useCallback(async (
        file: File,
        number: string,
        caption?: string,
        requestId?: string,
        onAccepted?: () => void,
    ): Promise<UploadResult & SendMediaResult> => {
        const type = getMediaType(file);
        // O job precisa sobreviver ao fechamento da aba, com arquivo acessível ao servidor.
        const url = await uploadToR2(file);
        const sendResult = await sendViaUazapi(type, url, number, file.name, file.type, caption, requestId, onAccepted);

        return {
            url,
            type,
            ...sendResult,
        };
    }, [getMediaType, uploadToR2, sendViaUazapi]);

    return {
        getMediaType,
        uploadToR2,
        sendViaUazapi,
        uploadAndSend
    };
}
